import { SITE_TIME_ZONE } from "@/lib/site-time";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { AdminNav } from "@/components/admin/AdminNav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { updateDesignFeedbackStatus } from "@/lib/feedback.functions";
import { designFeedbackQuery } from "@/lib/feedback-queries";

export const Route = createFileRoute("/_authenticated/admin/feedback")({
  staticData: { sitemap: false },
  head: () => ({ meta: [
    { title: "Feedback ontwerpvoorstellen — Gopro Projects" },
    { name: "description", content: "Bewaarde feedback op de ontwerpvoorstellen voor de website." },
    { name: "robots", content: "noindex, nofollow" },
    { property: "og:title", content: "Feedback ontwerpvoorstellen — Gopro Projects" },
    { property: "og:description", content: "Bewaarde feedback op de ontwerpvoorstellen voor de website." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: AdminFeedbackPage,
});

function AdminFeedbackPage() {
  const [status, setStatus] = useState<"alle" | "nieuw" | "behandeld">("alle");
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery(designFeedbackQuery);
  const update = useMutation({
    mutationFn: (input: { id: string; status: "nieuw" | "behandeld" }) => updateDesignFeedbackStatus({ data: input }),
    onSuccess: () => {
      toast.success("Status bijgewerkt.");
      void queryClient.invalidateQueries({ queryKey: ["design-feedback"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Wijzigen mislukt."),
  });
  const filtered = data?.filter((item) => status === "alle" || item.status === status) ?? [];

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6">
      <AdminNav />
      <h1 className="text-3xl font-extrabold">Feedback ontwerpvoorstellen</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        De ontwerpvoorstellen zelf zijn verwijderd. De ontvangen feedback blijft hier bewaard.
      </p>
      <div className="mt-8 flex gap-2">
        {(["alle", "nieuw", "behandeld"] as const).map((value) => (
          <Button key={value} size="sm" variant={status === value ? "default" : "outline"} onClick={() => setStatus(value)}>
            {value[0]?.toUpperCase()}
            {value.slice(1)}
          </Button>
        ))}
      </div>
      {isLoading && <p className="mt-5 text-sm text-muted-foreground">Feedback laden…</p>}
      {isError && <p className="mt-5 text-sm text-destructive">Feedback kon niet geladen worden.</p>}
      {!isLoading && !isError && filtered.length === 0 && (
        <p className="mt-5 text-sm text-muted-foreground">Geen feedback in deze selectie.</p>
      )}
      <div className="mt-5 space-y-3">
        {filtered.map((item) => (
          <Card key={item.id}>
            <CardContent className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex gap-2">
                    <Badge>{`Voorstel ${item.variant.toUpperCase()}`}</Badge>
                    <Badge variant="secondary">{item.page}</Badge>
                  </div>
                  <h2 className="mt-3 font-semibold">
                    {item.name} ·{" "}
                    <a className="text-primary hover:underline" href={`mailto:${item.email}`}>
                      {item.email}
                    </a>
                  </h2>
                  <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">{item.message}</p>
                  <p className="mt-3 text-xs text-muted-foreground">
                    {new Intl.DateTimeFormat("nl-BE", { dateStyle: "medium", timeStyle: "short", timeZone: SITE_TIME_ZONE }).format(new Date(item.created_at))}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={update.isPending}
                  onClick={() => update.mutate({ id: item.id, status: item.status === "nieuw" ? "behandeld" : "nieuw" })}
                >
                  {item.status === "nieuw" ? (
                    <>
                      <Circle />
                      Markeer behandeld
                    </>
                  ) : (
                    <>
                      <CheckCircle2 />
                      Heropenen
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </main>
  );
}
